// feedback: the toast, the unsent-SMS preview, server messages.
import { $, $$, esc, icon, play } from './core.ts';
import { flash } from './flash.ts';

/** The kinds of toast; 'loading' has a spinner and no timer. */
export type ToastType = 'success' | 'danger' | 'warning' | 'info' | 'sms' | 'loading';

export interface ToastOptions {
  type?: ToastType;
  title?: string;
  /** ms before it closes by itself; 0 = stays until closed. Default per type. */
  timeout?: number;
  action?: { label: string; run: () => void; className?: string };
}

/** A shown toast: change its text (a loading toast becoming the result) or close it. */
export interface ToastHandle {
  update(m: string, o?: ToastOptions): void;
  close(): void;
}

/** A message the server sends for the toast (TempData bridge, SMS preview). */
interface ServerMessage {
  text: string;
  type?: ToastType;
  title?: string;
}

// ---------------- feedback: toast ----------------
// 'sms' (T-128): an SMS that was not really sent (development, staging), shown until closed so a code can be read
const T_ICON: Record<Exclude<ToastType, 'loading'>, string> = {
  success: 'check-circle',
  danger: 'x-circle',
  warning: 'alert',
  info: 'bell',
  sms: 'sms',
};
const T_TIME: Record<ToastType, number> = { success: 4000, info: 6000, warning: 8000, danger: 0, loading: 0, sms: 0 };
export function toast(msg: string, opt: ToastOptions = {}): ToastHandle {
  opt = { type: 'success', ...opt };
  let host = $('.ds-toasts');
  if (!host) {
    host = document.createElement('div');
    host.className = 'ds-toasts';
    host.setAttribute('aria-live', 'polite');
    document.body.appendChild(host);
  }
  const el = document.createElement('div');
  let timer: ReturnType<typeof setTimeout> | null = null,
    left = 0,
    started = 0;
  function render(): void {
    const t = opt.type!,
      time = opt.timeout ?? T_TIME[t];
    el.className = 'ds-toast ' + t;
    el.setAttribute('role', t === 'danger' ? 'alert' : 'status');
    el.innerHTML =
      `<span class="ic">${t === 'loading' ? '<span class="ds-spin" aria-hidden="true"></span>' : icon(T_ICON[t])}</span>` +
      `<span class="tx"><b>${esc(opt.title || msg)}</b>${opt.title ? `<p>${esc(msg)}</p>` : ''}</span>` +
      `<button type="button" class="x" aria-label="بستن پیام">${icon('close')}</button>` +
      (opt.action
        ? `<div class="acts"><button type="button" class="btn btn-sm ${t === 'danger' ? 'btn-danger' : 'btn-secondary'}${opt.action.className ? ' ' + esc(opt.action.className) : ''}">${esc(opt.action.label)}</button></div>`
        : '') +
      (time ? '<span class="tbar"></span>' : '');
    const bar = el.querySelector<HTMLElement>('.tbar');
    if (bar) bar.style.animationDuration = time + 'ms'; // CSSOM, not a style attribute (CSP)
    el.querySelector<HTMLElement>('.x')!.onclick = close;
    if (opt.action)
      el.querySelector<HTMLElement>('.acts .btn')!.onclick = () => {
        close();
        opt.action!.run();
      };
    $$('.ds-ico', el).forEach(play);
    clearTimeout(timer!);
    left = time;
    if (time) arm();
  }
  function arm(): void {
    started = Date.now();
    timer = setTimeout(close, left);
  }
  el.addEventListener('mouseenter', () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
    left -= Date.now() - started;
  });
  el.addEventListener('mouseleave', () => {
    if (left > 0 && !timer && (opt.timeout ?? T_TIME[opt.type!])) arm();
  });
  // the close animation folds the capsule back into a ball (ds.css ds-tout-close, 340 ms)
  function close(): void {
    clearTimeout(timer!);
    if (!el.isConnected) return;
    el.classList.add('out');
    setTimeout(() => el.remove(), 350);
  }
  render();
  host.appendChild(el);
  while (host.children.length > 3) host.firstElementChild!.remove();
  return {
    update(m, o) {
      msg = m;
      opt = { ...opt, title: undefined, action: undefined, timeout: undefined, ...(o || {}) };
      render();
    },
    close,
  };
}
// T-128: a script call's unsent SMS arrive in this header (SmsPreviewFilter; never set in Production). Shown now and kept
// for the next page, since the sign-in pages move on right after the call that sent the code.
const SMS_HEADER = 'X-DS-Sms-Preview';
function smsPreview(raw: string | null): void {
  if (!raw) return;
  try {
    (JSON.parse(decodeURIComponent(raw)) as ServerMessage[]).forEach(m => {
      toast(m.text, { type: 'sms', title: m.title });
      flash(m.text, { type: 'sms', title: m.title });
    });
  } catch {
    /* malformed: ignore */
  }
}
export function initSmsPreview(): void {
  const original = window.fetch.bind(window);
  // same-origin answers only: another site's header could fake a «test SMS» (security review)
  window.fetch = async (...args) => {
    const res = await original(...args);
    try {
      if (new URL(res.url, location.href).origin === location.origin) smsPreview(res.headers.get(SMS_HEADER));
    } catch {
      /* opaque answer */
    }
    return res;
  };
  window.jQuery?.(document).ajaxComplete((e, xhr) => smsPreview(xhr.getResponseHeader(SMS_HEADER)));
}
export function initFeedback(): void {
  // messages from the server (TempData bridge, <ds-feedback />)
  const data = $('#ds-feedback');
  if (data) {
    try {
      (JSON.parse(data.textContent) as ServerMessage[]).forEach(m =>
        toast(m.text, { type: m.type, title: m.title || undefined }),
      );
    } catch {
      /* malformed: ignore */
    }
  }
  $$('.alert [data-ds-dismiss]').forEach(b => b.addEventListener('click', () => b.closest('.alert')!.remove()));
}
